import React, { useCallback, useState } from "react";
import { checkInProduct, createJob } from "../../api/migration";
import type { ILnp } from "../../type";
import MigrationBrowserPanel from "./components/MigrationBrowserPanel";
import MigrationCartPanel, {
  CartRow,
} from "./components/MigrationCartPanel";
import MigrationBatchBrowserPanel from "./components/MigrationBatchBrowserPanel";
import MigrationJobList from "./components/MigrationJobList";
import MigrationJobDetailModal from "./components/MigrationJobDetailModal";
import MigrationReviewModal from "./components/MigrationReviewModal";
import MigrationDashboard from "./components/MigrationDashboard";
import { extractApiError } from "./helpers";
import useMigrationJobs from "./hooks/useMigrationJobs";

interface Props {
  active: boolean;
  showConfirm: (config: {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    cancelLabel?: string;
    variant?: "primary" | "danger" | "warning";
    onConfirm: () => void;
    onCancel?: () => void;
  }) => void;
  showInfo: (config: {
    title: string;
    message: React.ReactNode;
    variant?: "success" | "info" | "warning";
  }) => void;
}

interface ReviewState {
  open: boolean;
  cartList: CartRow[];
  scheduledAt: string | null;
}

const REVIEW_DEFAULT: ReviewState = {
  open: false,
  cartList: [],
  scheduledAt: null,
};

/**
 * Migration tab: two-pane cart UI for composing a copy job, plus the
 * existing job list and detail modal below.
 *
 * Cart state lives here (not in a Zustand store) because it is inherently
 * tied to "one create-job composition in progress" and should not survive
 * a page refresh. The Browser panel pushes rows into the cart via `onAdd`
 * / `onAddMany`; the Cart panel pulls them via `cart` for rendering.
 *
 * `inProductPks` accumulates every beta PK confirmed to already exist on
 * product (browser-panel page loads + cart bulk-adds both contribute).
 * This drives the "prod" badges so admins see redundancy before they
 * commit; correctness itself still comes from the backend's SKIPPED
 * bookkeeping.
 */
const MigrationTab: React.FC<Props> = ({ active, showConfirm, showInfo }) => {
  const jobs = useMigrationJobs(active);
  const [detailJobId, setDetailJobId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<"row" | "batch">("row");
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  const [cart, setCart] = useState<Map<number, CartRow>>(() => new Map());
  const [inProductPks, setInProductPks] = useState<Set<number>>(
    () => new Set()
  );
  const [reviewState, setReviewState] = useState<ReviewState>(REVIEW_DEFAULT);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addToCart = useCallback((row: ILnp) => {
    setCart((prev) => {
      if (prev.has(row.index)) return prev;
      const next = new Map(prev);
      next.set(row.index, {
        index: row.index,
        experiment_id: row.experiment_id,
        il_name: row.il_name,
        cargo: row.cargo,
      });
      return next;
    });
  }, []);

  const addManyToCart = useCallback(async (rows: ILnp[]) => {
    if (rows.length === 0) return;
    setCart((prev) => {
      const next = new Map(prev);
      rows.forEach((row) => {
        if (!next.has(row.index)) {
          next.set(row.index, {
            index: row.index,
            experiment_id: row.experiment_id,
            il_name: row.il_name,
            cargo: row.cargo,
          });
        }
      });
      return next;
    });
    // Background in-product check — the browser panel's per-page check
    // doesn't cover bulk-added rows from deeper pages.
    try {
      const pks = rows.map((r) => r.index);
      const hits = await checkInProduct(pks);
      setInProductPks((prev) => {
        const next = new Set(prev);
        hits.forEach((v) => next.add(v));
        return next;
      });
    } catch {
      // Non-fatal — badges are optional.
    }
  }, []);

  const removeFromCart = useCallback((pk: number) => {
    setCart((prev) => {
      if (!prev.has(pk)) return prev;
      const next = new Map(prev);
      next.delete(pk);
      return next;
    });
  }, []);

  const removeManyFromCart = useCallback((pks: number[]) => {
    if (pks.length === 0) return;
    setCart((prev) => {
      const next = new Map(prev);
      pks.forEach((pk) => next.delete(pk));
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart(new Map());
  }, []);

  const cartInCartPks = React.useMemo(() => new Set(cart.keys()), [cart]);

  const openReview: React.ComponentProps<
    typeof MigrationCartPanel
  >["onReview"] = useCallback(({ cartList, scheduledAt }) => {
    setReviewState({
      open: true,
      cartList,
      scheduledAt,
    });
  }, []);

  const handleReviewCancel = useCallback(() => {
    if (isSubmitting) return;
    setReviewState(REVIEW_DEFAULT);
  }, [isSubmitting]);

  const handleReviewProceed = useCallback(async () => {
    if (!reviewState.open) return;
    const pks = reviewState.cartList.map((r) => r.index);
    if (pks.length === 0) return;
    setIsSubmitting(true);
    try {
      const job = await createJob({
        selected_pks: pks,
        selection: { filter: { index: pks }, search: "" },
        scheduled_at: reviewState.scheduledAt,
        conflict_resolution: "merge",
      });
      showInfo({
        title: "Migration job created",
        message: `Job #${job.id} ${reviewState.scheduledAt ? "scheduled" : "dispatched"} for ${job.total_count} row(s).`,
        variant: "success",
      });
      clearCart();
      setReviewState(REVIEW_DEFAULT);
      void jobs.reload();
    } catch (err) {
      showInfo({
        title: "Create failed",
        message: extractApiError(err, "Create failed."),
        variant: "warning",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [reviewState, showInfo, clearCart, jobs]);

  return (
    <div className="space-y-4">
      <MigrationDashboard active={active} refreshKey={dashboardRefreshKey} />
      
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-gray-900">
            Create data copy job
          </h3>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("row")}
              className={`px-3 py-1 text-sm font-medium rounded-md ${
                viewMode === "row" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              By Lnp Row
            </button>
            <button
              onClick={() => setViewMode("batch")}
              className={`px-3 py-1 text-sm font-medium rounded-md ${
                viewMode === "batch" ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              By Upload Batch
            </button>
          </div>
        </div>
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_26rem] 2xl:grid-cols-[minmax(0,1fr)_30rem]">
          {viewMode === "row" ? (
            <MigrationBrowserPanel
              inCartPks={cartInCartPks}
              onAdd={addToCart}
              onAddMany={addManyToCart}
              showInfo={showInfo}
              active={active}
            />
          ) : (
            <MigrationBatchBrowserPanel
              inCartPks={cartInCartPks}
              onAddMany={addManyToCart}
              showInfo={showInfo}
              showConfirm={showConfirm}
              active={active}
              onRefreshDashboard={() => setDashboardRefreshKey(k => k + 1)}
            />
          )}
          <MigrationCartPanel
            cart={cart}
            onRemove={removeFromCart}
            onRemoveMany={removeManyFromCart}
            onClear={clearCart}
            inProductPks={inProductPks}
            showConfirm={showConfirm}
            onReview={openReview}
          />
        </div>
      </section>

      <MigrationJobList
        jobs={jobs.jobs}
        count={jobs.count}
        page={jobs.page}
        setPage={jobs.setPage}
        isLoading={jobs.isLoading}
        error={jobs.error}
        reload={jobs.reload}
        onOpenDetail={(job) => setDetailJobId(job.id)}
        showConfirm={showConfirm}
        showInfo={showInfo}
      />
      <MigrationJobDetailModal
        jobId={detailJobId}
        onClose={() => setDetailJobId(null)}
      />
      <MigrationReviewModal
        open={reviewState.open}
        cartList={reviewState.cartList}
        inProductPks={inProductPks}
        scheduledAt={reviewState.scheduledAt}
        isSubmitting={isSubmitting}
        onProceed={handleReviewProceed}
        onCancel={handleReviewCancel}
      />
    </div>
  );
};

export default MigrationTab;
